import { useState, useEffect, useContext, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import 'bootstrap/dist/css/bootstrap.min.css';

// Styles
import '../App.css';
import '../Asset/Mobile/Responsive.css';

// Atoms & Context
import { useAtomValue, useSetAtom } from 'jotai';
import { userInfoAtom } from '../data/atoms/utilsAtoms';
import { VideoContext } from '../Pages/VideoPlayer/VideoContext';
import { userReactionAtom } from '../data/atoms/reactionAtom';
import { getSignupRoute } from '../constants/featureFlags';

// API & SWR
import api from '../data/api';
import videoApi from '../api/videoApi';
import checkApiStatus from './SubscripStatus';
import { useFetchActiveBanner } from '../data/swrs/banners';

// Lazy-loaded Components
const MobileViewSearch = lazy(() => import('./MobileViewSearch'));
const AddToPlayListModal = lazy(() => import('./playlist/personal-playlist/AddToPlayListModal'));
const VideoThumbnailCard = lazy(() => import('../v2/components/VideoThumbnailCard'));
import PopUpModal from './PopupModal/PopUpModal';

function VideoSection() {
	const setUserReactions = useSetAtom(userReactionAtom);
	axios.defaults.withCredentials = true;
	const redirect = useNavigate();
	const location = useLocation();
	const [responseData, setResponseData] = useState([]);
	const [modal, setModal] = useState(false);
	const { selectedVideo } = useContext(VideoContext);
	const [videoData, setVideoData] = useState({});
	const [videoData2, setVideoData2] = useState({});
	const [isHomeLoading, setIsHomeLoading] = useState(true);
	const [homeLoadError, setHomeLoadError] = useState('');
	const { data: bannerImage, isLoading: isBannerLoading } = useFetchActiveBanner();
	const activeBannerImage = bannerImage?.data?.image;

	const [mobileActiveTab, setMobileActiveTab] = useState('Recent');

	const [savedVideoIds, setSavedVideoIds] = useState([]);
	const userData = useAtomValue(userInfoAtom);
	const setUserData = useSetAtom(userInfoAtom);

	// Sync subscription/user-type data into atom on home page load,
	// but do NOT redirect unsubscribed users — they can freely browse the home page.
	// Redirecting to /plans only happens when the user tries to play a video (see handlePlayerVideo).
	useEffect(() => {
		if (!userData.token || !['/', '/home'].includes(location.pathname.toLowerCase())) {
			return;
		}

		let isActive = true;

		const syncSubscriptionData = async () => {
			// Call checkApiStatus to update the userInfoAtom (subscription state, userType, isTrialAvailed, etc.)
			// We deliberately ignore the boolean return value here — we only want the side-effect of
			// refreshing the atom so that other parts of the UI (e.g. handlePlayerVideo) have fresh data.
			await checkApiStatus(userData.token, setUserData);
		};

		if (isActive) {
			syncSubscriptionData();
		}

		return () => {
			isActive = false;
		};
	}, [location.pathname, setUserData, userData.token]);

	// Get filtered data for mobile view based on active tab
	const getMobileFilteredData = () => {
		if (mobileActiveTab === 'Saved' && userData.token) {
			return responseData.filter(category =>
				category.videos.some(
					video => video.savedvideo || (savedVideoIds?.length > 0 && savedVideoIds?.includes(video?._id))
				)
			);
		} else if (mobileActiveTab === 'Featured') {
			return responseData.filter(category => category.name.includes('Featured'));
		} else {
			// For Recent tab (default)
			return responseData.filter(category => category.name.includes('Recent'));
		}
	};

	const mobileFilteredData = getMobileFilteredData();

	useEffect(() => {
		if (userData.token) {
			videoApi
				.getAllSavedVideoIds(userData.user?.userId)
				.then(res => {
					setSavedVideoIds(res.data.savedVideos);
				})
				.catch(error => {
					console.error('Error fetching saved videos: ', error);
					setSavedVideoIds([]);
				});
		}
	}, [userData.token, userData.user?.userId]);

	useEffect(() => {
		if (selectedVideo) {
			setVideoData(selectedVideo);
		} else {
			const data = window.localStorage.getItem('selectedVideo');
			if (data) {
				setVideoData(JSON.parse(data));
			}
		}
	}, [selectedVideo]);

	// ----------| Add TO Playlist |--------------

	const handelSubmitPlaylist = async _id => {
		//setModal(true)
		try {
			const response = await api.videoPlayerPage(_id, userData.token);
			if (response.status === 200) {
				setModal(false);
				const jsonData = response.data[0];
				setVideoData2(jsonData);
				setModal(true);
			}
		} catch (error) {
			setModal(false);
			toast.error('Something went wrong', {
				position: 'top-center',
				duration: 3000
			});
		}
	};

	//--------for video player page------
	const handlePlayerVideo = async id => {
		const apistatus = await checkApiStatus(userData.token, setUserData);

		if (userData.token) {
			if (apistatus) {
				redirect(`/PlayerPage?v=${id}`);
			} else {
				//------- User is logged in but not subscribed, redirect to PlanPage----------
				redirect('/Plans');
			}
		} else {
			//---------User is not logged in, redirect to SignUp------------
			redirect(getSignupRoute());
		}
	};

	const handleSubmit = async (id, video) => {
		const isLiked =
			video.videoDetails?.savedvideo ||
			video.savedvideo ||
			(savedVideoIds?.length > 0 && savedVideoIds?.includes(video?.videoDetails?._id || video?._id));

		if (isLiked) {
			try {
				const response = await api.unsaveWishlist(id, userData.token);
				//console.log(response.data);
				const uniqueIdentifier = id;
				if (response.status === 200) {
					toast.success('Unsaved', {
						position: 'top-center',
						duration: 3000
					});
					const updatedResponseData = responseData.map(item => {
						const updatedVideos = item.videos.map(video => {
							if (video?._id === uniqueIdentifier) {
								return {
									...video,
									savedvideo: false // Set savedvideo to false for the matched video
								};
							}
							return video;
						});
						return {
							...item,
							videos: updatedVideos
						};
					});

					// Update the state (setResponseData) with the updated responseData
					setResponseData(updatedResponseData);
				}
			} catch (error) {
				console.error('Error fetching data: ', error);
			}
		} else {
			try {
				const response = await api.saveWishlist(id, userData.token);
				if (response.status === 200) {
					toast.success('Saved', {
						position: 'top-center',
						duration: 3000
					});
					//console.log(response.data);
					const uniqueIdentifier = id;
					const updatedResponseData = responseData.map(item => {
						const updatedVideos = item.videos.map(video => {
							if (video?._id === uniqueIdentifier) {
								return {
									...video,
									savedvideo: true // Set savedvideo to false for the matched video
								};
							}
							return video;
						});
						return {
							...item,
							videos: updatedVideos
						};
					});

					setResponseData(updatedResponseData);
				}
			} catch (error) {
				console.error('Error fetching data: ', error);
			}
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			setIsHomeLoading(true);
			setHomeLoadError('');

			try {
				let response;
				if (userData?.token) {
					response = await api.getHomeUsers(userData.token);
					console.log('video response', response);
				} else {
					response = await api.getHome();
				}

				if (response.status === 200) {
					const userReactionsSection = response.data.find(section => section.name === 'User Reactions');
					if (userReactionsSection && userReactionsSection.videos?.length) {
						const reactionMap = {};
						userReactionsSection.videos.forEach(r => {
							reactionMap[r.videoId] = r.reaction;
						});
						console.log(reactionMap);
						setUserReactions(reactionMap);
					}

					const sortedData = response.data.sort((a, b) => {
						if (a.name === 'Recent Releases') return -1;
						if (b.name === 'Recent Releases') return 1;
						return 0;
					});
					setResponseData(sortedData);
				}
			} catch (error) {
				console.error('Error fetching data: ', error);
				setResponseData([]);
				setHomeLoadError('We could not load the videos right now. Please check your connection and try again.');
			} finally {
				setIsHomeLoading(false);
			}
		};
		fetchData();
	}, [setUserReactions, userData.token]);

	const handleShowMoreClick = () => {
		redirect('/saved');
	};

	// Handle tab click for mobile view
	const handleMobileTabClick = tabId => {
		setMobileActiveTab(tabId);
	};
	return (
		<>
			<Helmet>
				<title>Online Dance Training & Lessons | The Godfrey Method On Demand</title>
				<meta name="description" content="Welcome to The Godfrey Method - On Demand" />
			</Helmet>
			<div className="row m-0 c-mobile_honm-serch">
				<Suspense fallback={<div>Loading mobile search...</div>}>
					<MobileViewSearch />
				</Suspense>
			</div>
			<div className="container-c-section">
				{userData.token ? (
					<div className="c-join-section">
						<h1>{userData.user ? `Welcome, ${userData?.user?.firstname} ${userData?.user?.lastname}.` : ''}</h1>
					</div>
				) : (
					<div className="c-join-section">
						<h1>Join The Community and Access Exclusive Content</h1>
					</div>
				)}
			</div>
			{/* Banner */}

			{isBannerLoading && !activeBannerImage && (
				<div className="c-banner-img c-banner-img--loading" aria-label="Loading banner">
					<div className="c-banner-loader" />
				</div>
			)}

			{activeBannerImage && (
				<div className="c-banner-img">
					<img src={activeBannerImage} width="1450" height="420" alt="Main banner" />
				</div>
			)}
			<div className="c-tab_cata c-tab_list-sa">
				<div className="row ml-0 mr-0 c-tab_box c-tab_list-sasa">
					<div className="col-md-8">
						<div className="c-tab_list">
							{/* Mobile-specific navigation with improved tab handling */}
							<ul className="nav nav-tabs Name d-lg-none">
								<li>
									<a
										className={`c-tab_mobile-list ${mobileActiveTab === 'Recent' ? 'active' : ''}`}
										onClick={() => handleMobileTabClick('Recent')}
									>
										Recent
									</a>
								</li>
								<li>
									<a
										className={mobileActiveTab === 'Featured' ? 'active' : ''}
										onClick={() => handleMobileTabClick('Featured')}
									>
										Featured
									</a>
								</li>

								{userData.token && (
									<li>
										<a
											className={`c-tab_list-border-l ${mobileActiveTab === 'Saved' ? 'active' : ''}`}
											onClick={() => handleMobileTabClick('Saved')}
										>
											Saved
										</a>
									</li>
								)}
							</ul>
						</div>
					</div>
				</div>
			</div>
			<div>
				{isHomeLoading && (
					<div className="c-video">
						<div className="row m-0 c-video_textrow">
							<div className="col-md-8 c-video_textcol">
								<h4>Loading videos...</h4>
							</div>
						</div>
					</div>
				)}
				{homeLoadError && !isHomeLoading && (
					<div className="c-video">
						<div className="row m-0 c-video_textrow">
							<div className="col-md-8 c-video_textcol">
								<h4>{homeLoadError}</h4>
							</div>
						</div>
					</div>
				)}
				{/* Mobile View Content - Only visible on small screens */}
				<div className="d-lg-none">
					<div className="tab-content">
						<div className="tab-pane fade in active show">
							{mobileFilteredData.map((category, index) => (
								<div
									style={{ scrollMarginTop: '6rem' }}
									key={`mobile-${index}`}
									id={`mobile-${category.name.split(' ')[0]}`}
								>
									<div className="c-video">
										<div className="row m-0 c-video_textrow">
											<div className="col-md-8 c-video_textcol">
												{category.name !== 'User Reactions' && <h4>{category.name}</h4>}
											</div>
											<div className="col-md-4 c-video_textcol">
												{/* <p className="c-view_more" onClick={() => handleShowMoreClick(category.name)}>View all </p> */}
											</div>
										</div>
									</div>
									{/* // Video card starts from here */}
									<div className="c-video_list">
										<div className="row m-0">
											{(category.name.includes('Recent') || category.name.includes('Featured') ? category.videos.slice(0, 6) : category.videos).map(
												(video, vIndex) => (
													<div key={`mobile-video-${vIndex}`} className="mb-2">
														<Suspense fallback={<div>Loading thumbnail...</div>}>
															<VideoThumbnailCard
																key={`mobile-thumbnail-${vIndex}`}
																content={video}
																isLoggedIn={userData?.token}
																handlePlayerVideo={handlePlayerVideo}
																handelSubmitPlaylist={handelSubmitPlaylist}
																handleSaveUnsave={handleSubmit}
																savedVideos={savedVideoIds}
																modal={modal}
																setModal={setModal}
																setVideoData={setVideoData2}
																titleStyles={{ fontSize: 'clamp(1.15rem, 1.2vw, 1.6rem)' }}
															/>
														</Suspense>
													</div>
												)
											)}
										</div>
									</div>
									{mobileActiveTab === 'Recent' && index === 0 && (
										<div className="c-mobile_show-btn">
											<button onClick={() => handleShowMoreClick(category.name)}>Show More</button>
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				</div>

				{/* Desktop View Content - Original implementation preserved */}
				<div className="d-none d-lg-block">
					<div className="tab-content">
						<div id="home" className="tab-pane fade in active show">
							{responseData &&
								responseData?.map((category, index) => (
									<div style={{ scrollMarginTop: '6rem' }} key={index} id={category.name.split(' ')[0]}>
										<div key={index} className="c-tab_cata-text">
											{category.name.includes('Recent') ? (
												<div className="c-tab_cata c-tab_list-sa">
													<div className="row ml-0 mr-0 c-tab_box c-tab_list-sasa">
														<div className="c-tab_list">
															<ul className="nav nav-tabs Name">
																<li>
																	<a data-toggle="tab" className="c-tab_mobile-list" href="#Recent">
																		Recent
																	</a>
																</li>
																<li>
																	<a data-toggle="tab" href="#Featured">
																		Featured
																	</a>
																</li>
																{userData.token && (
																	<li>
																		<a data-toggle="tab" className="c-tab_list-border-l" href="#Saved">
																			Saved
																		</a>
																	</li>
																)}
															</ul>
														</div>
													</div>
												</div>
											) : (
												<span></span>
											)}
											{category.name !== 'User Reactions' && <h4>{category.name.split(' ')[0]}</h4>}
										</div>
										<div className="c-video">
											<div className="row m-0 c-video_textrow">
												<div className="col-md-8 c-video_textcol">
													{category?.name !== 'User Reactions' && <h4>{category.name.split(' ')[0]}</h4>}
												</div>
												<div className="col-md-4 c-video_textcol">
													{/* <p className="c-view_more" onClick={() => handleShowMoreClick(category.name)}>View all </p> */}
												</div>
											</div>
										</div>
										{/* // Video card starts from here */}
										<div className="c-video_list">
											<div className="row m-0">
												{category.name !== 'User Reactions' &&
													(category.name.includes('Recent') || category.name.includes('Featured') ? category.videos.slice(0, 6) : category.videos).map(
														(video, index) => (
															<div key={index} className="mb-2">
																<Suspense fallback={<div>Loading thumbnail...</div>}>
																	<VideoThumbnailCard
																		content={video}
																		isLoggedIn={userData?.token}
																		handlePlayerVideo={handlePlayerVideo}
																		handelSubmitPlaylist={handelSubmitPlaylist}
																		handleSaveUnsave={handleSubmit}
																		savedVideos={savedVideoIds}
																		modal={modal}
																		setModal={setModal}
																		setVideoData={setVideoData2}
																		titleStyles={{ fontSize: 'clamp(1.15rem, 1.2vw, 1.6rem)' }}
																	/>
																</Suspense>
															</div>
														)
													)}
											</div>

											{/* //////////////////////Mobile Show More Button //////////// */}
										</div>
										{index === 2 && (
											<div className="c-mobile_show-btn">
												<button onClick={() => handleShowMoreClick(category.name)}>Show More</button>
											</div>
										)}
									</div>
								))}
						</div>
					</div>
				</div>
			</div>

			<PopUpModal />
			<Suspense fallback={<div>Loading playlist modal...</div>}>
				<AddToPlayListModal modal={modal} setModal={setModal} activeObject={videoData2} viewData={videoData} />
			</Suspense>
		</>
	);
}

export default VideoSection;
